import { getBomPartQuantity, Part } from "../models/part";
import { Impact, ImpactElement } from "../models/impact";

/**
 * The where-used tree of a part: the part itself and, for every assembly that
 * contains it, how many times it is contained and that assembly's own tree.
 */
export interface ImpactData {
  part: Part;
  impacts: {
    count: number;
    impact: any;
  }[];
}

/**
 * Walk the assemblies upwards from a part and build its where-used tree.
 * `map` caches assemblies already visited, `set` stops the walk from looping
 * when an assembly ends up containing itself through another one.
 */
export function getImpactData(
  assemblies: Partial<Part>[],
  part: Part,
  map: Map<string, ImpactData> = new Map(),
  set: Set<string> = new Set()
): any {
  const partNumber = part.partNumber;
  if (set.has(partNumber)) {
    return null;
  }
  set.add(partNumber);
  const parts: Partial<Part>[] = assemblies.filter(
    (assembly) =>
      assembly.parts != null &&
      Object.keys(assembly.parts).indexOf(partNumber) !== -1
  );

  const partsArray: Partial<Part> = [
    ...Object.keys(parts).map(
      (pNumber: string) => parts[pNumber as keyof typeof parts]
    )
  ];
  return {
    part,
    impacts: partsArray.map((assembly: Part) => {
      const count = getBomPartQuantity(assembly.parts[partNumber]);
      let impact: any;
      if (map.has(assembly.partNumber)) {
        const impactData = map.get(assembly.partNumber);
        if (impactData !== undefined) {
          impact = impactData;
        }
      } else {
        impact = getImpactData(assemblies, assembly, map, set);
        if (impact !== null) {
          map.set(assembly.partNumber, impact);
        }
      }
      return { count, impact };
    })
  };
}

/**
 * Turn the where-used tree into impact documents, one per assembly, carrying
 * how many times the part appears in it.
 */
export function flatten(data: ImpactData, factor: number): ImpactElement[] {
  const impacts: ImpactElement[] = [];
  data.impacts.forEach((d) => {
    const count = d.count;
    if (d.impact == null) {
      return;
    }
    const part = d.impact.part;
    const impact: Impact = {
      type: "impact",
      omfVehicleName: part.description,
      omfVehicleFleet: "",
      omfVehicleClass: "",
      omfCommodityRespName: "",
      omfVehicleRespDep: "",
      omfVehicleRespEmail: "",
      omfVehicleCnt: null,
      omfNumber: "",
      artNumber: part.partNumber,
      level: part.level,
      vehicleGroup: "",
      impacts: flatten(d.impact, count * factor),
      productGroup: "",
      actions: "",
      user_id: "",
      designType: part.designType,
      sapStatus: part.sapStatus,
      stockRange: part.stockRange,
      verwa: part.verwa
    };
    impacts.push({ count, impact });
  });
  impacts.forEach((doc) => {
    if (doc !== undefined && doc !== null) {
      if (
        doc.impact.impacts != null &&
        doc.count != undefined &&
        doc.count !== 0
      ) {
        doc.impact.omfVehicleCnt = doc.count;
      } else {
        doc.impact.omfVehicleCnt = 1;
      }
    }
  });
  return impacts;
}

/**
 * The impacts of a part, flat, as they are stored on a case.
 */
export function getImpactsFlat(
  part: Part,
  assemblies: Partial<Part>[]
): Impact[] {
  const impactData = getImpactData(assemblies, part);
  return flatten(impactData, 1).map((e) => e.impact);
}
